import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

type ConfirmOptions = {
  title?: string;
  message?: string;
  highlightText?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  listTitle?: string;
  listItems?: { uid: string; name: string; description?: string }[];
};

type ConfirmDialogContextType = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
};

type ProviderProps = {
  children: ReactNode;
};

const ConfirmDialogContext = createContext<ConfirmDialogContextType | null>(null);

export const ConfirmDialogProvider = ({ children }: ProviderProps) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({});
  const [resolver, setResolver] = useState<(value: boolean) => void>(() => {});

  const confirm = (opts: ConfirmOptions) => {
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      setResolver(() => resolve);
    });
  };

  const handleCancel = () => {
    setOpen(false);
    resolver(false);
  };

  const handleConfirm = () => {
    setOpen(false);
    resolver(true);
  };

  // Check if it's a delete action
  const isDeleteAction = options.title?.toLowerCase().includes('delete');

  // Function to render message with highlighted text
  const renderMessage = () => {
    const { message = '', highlightText } = options;
    
    if (!highlightText) {
      return (
        <Typography
          sx={{
            fontSize: '15px',
            color: '#64748b',
            lineHeight: 1.6,
          }}
        >
          {message}
        </Typography>
      );
    }

    // Split message and highlight the specified text
    const parts = message.split(highlightText);
    
    return (
      <Typography
        sx={{
          fontSize: '15px',
          color: '#64748b',
          lineHeight: 1.6,
        }}
      >
        {parts[0]}
        <Box
          component="span"
          sx={{
            fontWeight: 700,
            color: '#1e293b',
          }}
        >
          {highlightText}
        </Box>
        {parts[1]}
      </Typography>
    );
  };

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}

      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: '420px',
            borderRadius: '16px',
            overflow: 'visible',
            p: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: '#64748b',
            '&:hover': {
              backgroundColor: '#f1f5f9',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Title */}
        <DialogTitle sx={{ p: 0, mb: 1.5, pr: 4 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '20px',
              color: '#1e293b',
              letterSpacing: '-0.5px',
            }}
          >
            {options.title || 'Confirm Action'}
          </Typography>
        </DialogTitle>

        {/* Message with highlighted text */}
        <DialogContent sx={{ p: 0, mb: 3 }}>
          {renderMessage()}
        </DialogContent>

        {/* Affected Items List */}
        {options.listItems && options.listItems.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
              {options.listTitle || 'Affected items'}
            </Typography>
            <Box sx={{ maxHeight: 160, overflowY: 'auto', bgcolor: '#f8fafc', borderRadius: '10px', p: 1.5 }}>
              {options.listItems.map((item) => (
                <Box key={item.uid} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#667eea', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{item.name}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Action Buttons */}
        <DialogActions
          sx={{
            p: 0,
            gap: 1.5,
            display: 'flex',
          }}
        >
          {/* Cancel Button */}
          <Button
            onClick={handleCancel}
            fullWidth
            sx={{
              borderRadius: '10px',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#e2e8f0',
                boxShadow: 'none',
                color: '#334155',
              },
              '&:focus': {
                color: '#475569',
              },
              '&:focus-visible': {
                outline: '2px solid #667eea',
                outlineOffset: '2px',
              },
            }}
          >
            {options.cancelLabel || 'Cancel'}
          </Button>

          {/* Confirm/Delete Button */}
          <Button
            onClick={handleConfirm}
            fullWidth
            sx={{
              borderRadius: '10px',
              background: isDeleteAction
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#ffffff',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: isDeleteAction
                ? '0 4px 6px -1px rgba(239, 68, 68, 0.3)'
                : '0 4px 6px -1px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: isDeleteAction
                  ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                  : 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                boxShadow: isDeleteAction
                  ? '0 6px 12px -2px rgba(239, 68, 68, 0.4)'
                  : '0 6px 12px -2px rgba(102, 126, 234, 0.4)',
                transform: 'translateY(-1px)',
                color: '#ffffff',
              },
              '&:focus': {
                color: '#ffffff',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              '&:focus-visible': {
                outline: '2px solid #667eea',
                outlineOffset: '2px',
              },
            }}
          >
            {options.confirmLabel || (isDeleteAction ? 'Delete' : 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmDialogContext.Provider>
  );
};

// --------------- Hook ----------------
export const useConfirm = () => {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmDialogProvider');
  }
  return ctx.confirm;
};
